// src/utils/retry.ts
// ────────────────────────────────────────────────────────────────────────────
// Shared retry / backoff policies — single source of truth for every FE
// retry-after-failure decision.
//
// Why this exists
// ───────────────
// Multiple call sites (React Query global retryDelay, `useVideoGallery`'s
// mutation retries, `sseReconnect`, `SharedScopeProvider`'s SSE reconnect
// table, `lazyWithRetry`, ad-hoc fetch retries) each hand-rolled a slightly
// different backoff formula. The variance silently affects user-visible
// SLOs (settle-time after a transient blip) and security posture (thundering-
// herd risk after a server-side outage). One lib, three named policies,
// every call site picks the right one.
//
// Industry reference
// ──────────────────
// The three policies match the AWS Architecture Blog "Exponential Backoff
// And Jitter" canonical taxonomy:
//
//   • **fullJitter**          (recommended default for retries)
//        delay = random(0, min(cap, base * 2^attempt))
//        Uniform spread over the entire window — best thundering-herd
//        avoidance for unrelated retry events.
//
//   • **equalJitter**         (mixed deterministic + random)
//        exp   = min(cap, base * 2^attempt)
//        delay = exp/2 + random(0, exp/2)
//        Preserves a minimum settle-time floor. Used when an immediate
//        retry would defeat the purpose (e.g. give the server room to
//        recover before the next attempt window opens).
//
//   • **exponentialPlusJitter** (legacy SSE pattern)
//        delay = min(cap, base * 2^attempt) + random(0, jitterMs)
//        Exponential floor with a small additive jitter ceiling. Used
//        by `sseReconnect.ts` and the SharedScopeProvider's `SSE_BACKOFF`
//        table. Kept for back-compat semantic parity.
//
//   • **decorrelatedJitter**   (AWS recommended for distributed clients)
//        delay = min(cap, random(base, prevDelay * 3))
//        Avoids long sequences of small or long delays. Best for high-
//        contention clusters; overkill for single-browser-tab retries.
//
// Design properties
// ─────────────────
// • Pure — no I/O, no module-scoped mutation. The only non-determinism
//   is `Math.random()` (replaceable in tests via `rng` injection).
// • SOLID
//   ▸ SRP — each function picks one backoff curve.
//   ▸ OCP — `BackoffOptions` extensible; `rng` injectable.
//   ▸ ISP — narrow surface (one function, one return type).
// • Bounded — `attempt` capped at 30 internally so `2^attempt` cannot
//   overflow even when callers pass garbage.
// ────────────────────────────────────────────────────────────────────────────

export interface BackoffOptions {
  /** Base delay in ms. Default 500. */
  baseMs?: number;
  /** Hard cap on the returned delay in ms. Default 30_000. */
  maxMs?: number;
  /**
   * Random source for unit tests. Default `Math.random`.
   * Must return a uniform value in [0, 1).
   */
  rng?: () => number;
}

export interface AdditiveJitterOptions extends BackoffOptions {
  /** Maximum additive jitter on top of the exponential floor. Default 1500. */
  jitterMs?: number;
}

const DEFAULT_BASE_MS = 500;
const DEFAULT_MAX_MS  = 30_000;
const DEFAULT_JITTER  = 1500;
const ATTEMPT_CAP     = 30;

function _exp(attempt: number, baseMs: number, maxMs: number): number {
  const a = Math.max(0, Math.min(ATTEMPT_CAP, Math.floor(attempt)));
  return Math.min(maxMs, baseMs * (2 ** a));
}

function _rng(opts?: BackoffOptions): () => number {
  return opts?.rng ?? Math.random;
}

/**
 * AWS "full jitter" — recommended default for FE retries.
 *
 *   delay = random(0, min(cap, base * 2^attempt))
 *
 * Mirrors the orchestrator's `internal/retry` policy so client and server
 * behave consistently under transient stress.
 *
 *   attempt 0 → [0,   500] ms   (base=500)
 *   attempt 1 → [0,  1000] ms
 *   attempt 2 → [0,  2000] ms
 *   attempt 3 → [0,  4000] ms
 *   …          (capped at maxMs)
 */
export function fullJitter(attempt: number, opts?: BackoffOptions): number {
  const baseMs = opts?.baseMs ?? DEFAULT_BASE_MS;
  const maxMs  = opts?.maxMs  ?? DEFAULT_MAX_MS;
  const exp    = _exp(attempt, baseMs, maxMs);
  return Math.floor(_rng(opts)() * (exp + 1));
}

/**
 * AWS "equal jitter" — half deterministic + half random. Preserves a
 * minimum settle-time floor (useful when an immediate retry would defeat
 * the purpose).
 *
 *   delay = exp/2 + random(0, exp/2)
 */
export function equalJitter(attempt: number, opts?: BackoffOptions): number {
  const baseMs = opts?.baseMs ?? DEFAULT_BASE_MS;
  const maxMs  = opts?.maxMs  ?? DEFAULT_MAX_MS;
  const exp    = _exp(attempt, baseMs, maxMs);
  const half   = Math.floor(exp / 2);
  return half + Math.floor(_rng(opts)() * (half + 1));
}

/**
 * Legacy SSE pattern: exponential floor + small additive random jitter.
 *
 *   delay = min(cap, base * 2^attempt) + random(0, jitterMs)
 *
 * Used by `sseReconnect.ts` and `SharedScopeProvider` for connection
 * recovery where preserving a known floor matters more than uniform
 * thundering-herd avoidance.
 */
export function exponentialPlusJitter(
  attempt: number,
  opts?: AdditiveJitterOptions,
): number {
  const baseMs   = opts?.baseMs   ?? 1000;
  const maxMs    = opts?.maxMs    ?? DEFAULT_MAX_MS;
  const jitterMs = opts?.jitterMs ?? DEFAULT_JITTER;
  const exp      = _exp(attempt, baseMs, maxMs);
  const jitter   = Math.floor(_rng(opts)() * jitterMs);
  return exp + jitter;
}

/**
 * AWS "decorrelated jitter" — recommended when many clients share the
 * same upstream and you want to avoid synchronized retry waves WITHOUT
 * a strict exponential progression.
 *
 *   delay = min(cap, random(base, prevDelay * 3))
 *
 * @param prevDelay Previous delay in ms (caller-tracked).
 */
export function decorrelatedJitter(
  prevDelay: number,
  opts?: BackoffOptions,
): number {
  const baseMs = opts?.baseMs ?? DEFAULT_BASE_MS;
  const maxMs  = opts?.maxMs  ?? DEFAULT_MAX_MS;
  const lo     = baseMs;
  const hi     = Math.max(lo + 1, Math.min(maxMs, prevDelay * 3));
  return Math.floor(lo + _rng(opts)() * (hi - lo));
}
